import type { Blur64ImageData } from 'blur64';
import { ComponentPropsWithoutRef } from 'react';
import { isVideoUrl } from '@/shared/lib/validations';
import { Optional } from '@/shared/model/Optional';
import { MediaImage } from './media-image';
import { MediaVideo } from './media-video';
import { Media as MediaType } from './types';

type Props = {
    data: MediaType;
    isInView?: boolean;
    priority?: boolean;
    blurData?: Blur64ImageData;
} & (Optional<ComponentPropsWithoutRef<typeof MediaVideo>, 'src' | 'alt'> &
    Omit<Optional<ComponentPropsWithoutRef<typeof MediaImage>, 'src' | 'alt'>, 'blurDataURL'>);

export const Media = ({ data, isInView = true, blurData, priority = false, ...props }: Props) => {
    if (!data.url) {
        return null;
    }

    const isVideo = isVideoUrl(data.url);

    if (isVideo) {
        return (
            <MediaVideo
                src={data.url}
                alt={data.alt}
                isInView={isInView}
                posterUrl={data.posterUrl}
                posterBlurData={blurData}
                priority={priority}
                {...props}
            />
        );
    }

    return (
        // eslint-disable-next-line @next/next/no-img-element -- storefront media URLs are arbitrary CDNs
        <img
            src={data.url}
            alt={data.alt}
            width={blurData?.width}
            height={blurData?.height}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={priority ? 'high' : 'auto'}
            {...props}
        />
    );
};
